import { cn } from "@/lib/utils/cn";
import { createElement, type ReactNode, type ElementType, type HTMLAttributes } from "react";

export type TypographyVariant =
  | "caption"
  | "overline"
  | "small"
  | "body"
  | "headline5"
  | "headline4"
  | "headline3"
  | "headline2"
  | "headline1"
  | "display2"
  | "display1";

type TypographyWeight = "normal" | "medium" | "semibold" | "bold";

type TypographyColor =
  | "default"
  | "secondary"
  | "muted"
  | "light"
  | "error"
  | "success"
  | "warning"
  | "white"
  | "inherit"
  | (string & {});

export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, "color"> {
  variant?: TypographyVariant;
  as?: ElementType;
  weight?: TypographyWeight;
  color?: TypographyColor;
  mono?: boolean;
  tabularNums?: boolean;
  truncate?: boolean;
  leading?: string;
  tracking?: string;
  htmlFor?: string;
  children: ReactNode;
  [key: string]: unknown;
}

const variantConfig: Record<TypographyVariant, { classes: string; defaultAs: ElementType; defaultColor: string }> = {
  caption:   { classes: "text-[10px] font-normal",                               defaultAs: "span", defaultColor: "text-gray-600" },
  overline:  { classes: "text-overline font-semibold uppercase tracking-wider",    defaultAs: "span", defaultColor: "text-gray-500" },
  small:     { classes: "text-xs font-normal",                                     defaultAs: "span", defaultColor: "text-gray-600" },
  body:      { classes: "text-sm font-normal",                                     defaultAs: "p",    defaultColor: "text-gray-900" },
  headline5: { classes: "text-base font-medium",                                   defaultAs: "h5",   defaultColor: "text-gray-900" },
  headline4: { classes: "text-lg font-semibold",                                   defaultAs: "h4",   defaultColor: "text-gray-900" },
  headline3: { classes: "text-xl font-semibold",                                   defaultAs: "h3",   defaultColor: "text-gray-900" },
  headline2: { classes: "text-2xl font-bold leading-tight",                        defaultAs: "h2",   defaultColor: "text-gray-900" },
  headline1: { classes: "text-3xl font-bold",                                      defaultAs: "h1",   defaultColor: "text-gray-900" },
  display2:  { classes: "text-4xl font-bold",                                      defaultAs: "h1",   defaultColor: "text-gray-900" },
  display1:  { classes: "text-5xl font-bold",                                      defaultAs: "h1",   defaultColor: "text-gray-900" },
};

const knownColors: Record<string, string> = {
  default:   "text-gray-900",
  secondary: "text-gray-700",
  muted:     "text-gray-600",
  light:     "text-gray-500",
  error:     "text-red-600",
  success:   "text-green-700",
  warning:   "text-amber-600",
  white:     "text-white",
  inherit:   "",
};

const weightMap: Record<TypographyWeight, string> = {
  normal:   "font-normal",
  medium:   "font-medium",
  semibold: "font-semibold",
  bold:     "font-bold",
};

export function Typography({
  variant = "body",
  as,
  weight,
  color,
  mono,
  tabularNums,
  truncate,
  leading,
  tracking,
  className,
  children,
  ...rest
}: TypographyProps) {
  const config = variantConfig[variant];
  const element = as ?? config.defaultAs;

  const resolvedColor = color
    ? (knownColors[color] ?? color)
    : config.defaultColor;

  return createElement(
    element,
    {
      className: cn(
        config.classes,
        resolvedColor,
        weight && weightMap[weight],
        mono && "font-[family-name:var(--font-geist-mono)]",
        tabularNums && "tabular-nums",
        truncate && "truncate",
        leading,
        tracking,
        className,
      ),
      ...rest,
    },
    children,
  );
}
